import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { supabase } from '../../../lib/supabase';

const CollectionPreview = () => {
  const [activeCollection, setActiveCollection] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [featuredCollections, setFeaturedCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFeaturedCollections();
  }, []);

  const loadFeaturedCollections = async () => {
    try {
      const { data, error } = await supabase?.from('collections')?.select(`
          id,
          name,
          description,
          collectible_type,
          total_value,
          item_count,
          user_profiles!collections_user_id_fkey(full_name, avatar_url),
          collectibles(id, name, primary_image_url, current_value, estimated_value)
        `)?.eq('is_public', true)?.gt('item_count', 0)?.order('total_value', { ascending: false })?.limit(4);

      if (!error && data?.length > 0) {
        const mapped = data?.map(col => {
          const featured = col?.collectibles?.[0];
          const owner = col?.user_profiles;
          return {
            id: col?.id,
            title: col?.name,
            owner: owner?.full_name || 'Collector',
            avatar: owner?.avatar_url || null,
            totalValue: `$${(parseFloat(col?.total_value) || 0)?.toLocaleString()}`,
            items: col?.item_count || 0,
            featured: featured ? {
              name: featured?.name,
              image: featured?.primary_image_url || null,
              value: `$${(parseFloat(featured?.current_value || featured?.estimated_value) || 0)?.toLocaleString()}`,
            } : null,
            category: col?.collectible_type?.replace(/-/g, ' ')?.replace(/\b\w/g, c => c?.toUpperCase()) || 'Collectibles',
            description: col?.description || 'A curated collection of rare and valuable items.',
          };
        });
        setFeaturedCollections(mapped);
      }
    } catch (err) {
      console.error('Error loading featured collections:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAutoPlaying || featuredCollections?.length === 0) return;
    const interval = setInterval(() => {
      setActiveCollection((prev) => (prev + 1) % featuredCollections?.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, featuredCollections?.length]);

  const handleCollectionChange = (index) => {
    setActiveCollection(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const currentCollection = featuredCollections?.[activeCollection];

  if (loading) {
    return (
      <section id="features-section" className="py-24 bg-gradient-to-b from-background to-muted/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">Featured Collections</h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-12 items-center animate-pulse">
            <div className="bg-card border border-border rounded-2xl p-8 h-96" />
            <div className="space-y-3">
              {[1,2,3,4]?.map(i => <div key={i} className="h-16 bg-card border border-border rounded-xl" />)}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (featuredCollections?.length === 0) {
    return (
      <section id="features-section" className="py-24 bg-gradient-to-b from-background to-muted/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">Featured Collections</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Be among the first to showcase your collection to the community.
            </p>
          </div>
          <div className="text-center py-16">
            <Icon name="Package" size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No public collections yet. Start building yours today!</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="features-section" className="py-24 bg-gradient-to-b from-background to-muted/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
              Featured Collections
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Discover how passionate collectors are building sophisticated portfolios with real-time insights and professional-grade tracking.
            </p>
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Collection Showcase */}
          <motion.div
            key={activeCollection}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="bg-card border border-border rounded-2xl p-8 premium-hover depth-card">
              {/* Collection Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <Image
                    src={currentCollection?.avatar}
                    alt={currentCollection?.owner}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="text-lg font-semibold text-card-foreground">
                      {currentCollection?.owner}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {currentCollection?.category} Collector
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-primary">
                    {currentCollection?.totalValue}
                  </div>
                </div>
              </div>

              {/* Featured Item */}
              {currentCollection?.featured && (
                <div className="relative mb-6 overflow-hidden rounded-xl">
                  <Image
                    src={currentCollection?.featured?.image}
                    alt={currentCollection?.featured?.name}
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h4 className="text-lg font-semibold text-foreground mb-2">
                      {currentCollection?.featured?.name}
                    </h4>
                    <div className="flex items-center justify-between">
                      <span className="text-xl font-bold text-primary">
                        {currentCollection?.featured?.value}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Collection Stats */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="text-center p-3 bg-muted/30 rounded-lg">
                  <div className="text-lg font-bold text-card-foreground">
                    {currentCollection?.items}
                  </div>
                  <div className="text-xs text-muted-foreground">Items</div>
                </div>
                <div className="text-center p-3 bg-muted/30 rounded-lg">
                  <div className="text-lg font-bold text-primary">
                    {currentCollection?.totalValue}
                  </div>
                  <div className="text-xs text-muted-foreground">Value</div>
                </div>
              </div>

              <p className="text-muted-foreground text-sm leading-relaxed">
                {currentCollection?.description}
              </p>
            </div>
          </motion.div>

          {/* Collection Navigation */}
          <div className="space-y-6">
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-foreground mb-4">
                Explore Collections
              </h3>
              <p className="text-muted-foreground">
                See how collectors across different categories are building remarkable portfolios.
              </p>
            </div>

            <div className="space-y-3">
              {featuredCollections?.map((collection, index) => (
                <motion.button
                  key={collection?.id}
                  onClick={() => handleCollectionChange(index)}
                  className={`w-full text-left p-4 rounded-xl transition-all duration-300 premium-hover ${
                    index === activeCollection
                      ? 'bg-primary/10 border border-primary/20' :'bg-card border border-border hover:bg-muted/30'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Image
                        src={collection?.avatar}
                        alt={collection?.owner}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <div className="font-semibold text-card-foreground">
                          {collection?.title}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {collection?.items} items • {collection?.category}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-primary">
                        {collection?.totalValue}
                      </div>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CollectionPreview;